import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { allowsFunctionalStorage } from "@/lib/cookieConsent";
import { getAuthToken } from "@/lib/authStorage";

const KEY = "autopark_favorites";
const DELTA_KEY = "autopark_favorite_deltas";
const FavoritesContext = createContext(null);

function readDeltas() {
  if (!allowsFunctionalStorage()) return {};
  try {
    return JSON.parse(localStorage.getItem(DELTA_KEY)) || {};
  } catch (_) {
    return {};
  }
}

function applyDelta(carId, delta) {
  if (!allowsFunctionalStorage()) return {};
  const d = readDeltas();
  const next = (d[carId] || 0) + delta;
  if (next === 0) delete d[carId];
  else d[carId] = next;
  localStorage.setItem(DELTA_KEY, JSON.stringify(d));
  return d;
}

function clearDelta(carId) {
  if (!allowsFunctionalStorage()) return;
  const d = readDeltas();
  if (!(carId in d)) return;
  delete d[carId];
  localStorage.setItem(DELTA_KEY, JSON.stringify(d));
}

export function FavoritesProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState(() => {
    if (!allowsFunctionalStorage()) return [];
    try {
      const raw = JSON.parse(localStorage.getItem(KEY)) || [];
      return raw.map((e) => (typeof e === "string" ? { id: e, price: null } : e));
    } catch (_) {
      return [];
    }
  });
  const [priceDrops, setPriceDrops] = useState({});
  const [countOverrides, setCountOverrides] = useState({});
  const checked = useRef(false);
  const syncedUser = useRef(null);

  useEffect(() => {
    if (!allowsFunctionalStorage()) return;
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    const reload = () => {
      if (!allowsFunctionalStorage()) return;
      try {
        const raw = JSON.parse(localStorage.getItem(KEY)) || [];
        setItems(raw.map((e) => (typeof e === "string" ? { id: e, price: null } : e)));
      } catch {
        /* ignore */
      }
    };
    window.addEventListener("autopark:cookie-consent", reload);
    return () => window.removeEventListener("autopark:cookie-consent", reload);
  }, []);

  useEffect(() => {
    if (!user?.id) {
      syncedUser.current = null;
      return;
    }
    if (syncedUser.current === user.id) return;
    syncedUser.current = user.id;
    api
      .get("/my/favorites")
      .then((r) => {
        const serverItems = r.data?.items || [];
        if (serverItems.length > 0) setItems(serverItems);
      })
      .catch(() => {
        /* régi backend — helyi kedvencek maradnak */
      });
  }, [user?.id]);

  useEffect(() => {
    if (checked.current || items.length === 0) return;
    checked.current = true;
    api
      .get("/cars")
      .then((r) => {
        const byId = Object.fromEntries(r.data.map((c) => [c.id, c]));
        const drops = {};
        items.forEach((it) => {
          const c = byId[it.id];
          if (c && it.price != null && c.price < it.price) drops[it.id] = { old: it.price, current: c.price };
        });
        const n = Object.keys(drops).length;
        if (n > 0) {
          setPriceDrops(drops);
          toast.success(n === 1 ? "Jó hír! Egy kedvenc autód ára csökkent." : `Jó hír! ${n} kedvenc autód ára csökkent.`, {
            description: "Nézd meg a Kedvenceim oldalon.",
            duration: 8000,
          });
        }
      })
      .catch(() => {});
  }, [items]);

  const resolveCount = useCallback(
    (carId, fallback = 0) => {
      const base = carId in countOverrides ? countOverrides[carId] : Math.max(0, Number(fallback) || 0);
      const delta = readDeltas()[carId] || 0;
      return Math.max(0, base + delta);
    },
    [countOverrides],
  );

  const getFavoriteCount = useCallback(
    (carId, fallback = 0) => resolveCount(carId, fallback),
    [resolveCount],
  );

  const toggleFavoriteLocal = (car, wasFav) => {
    applyDelta(car.id, wasFav ? -1 : 1);
    setItems((prev) =>
      wasFav
        ? prev.filter((i) => i.id !== car.id)
        : [...prev, { id: car.id, price: car.price ?? null }],
    );
    const favoriteCount = resolveCount(car.id, car.favorite_count);
    return { favorited: !wasFav, favorite_count: favoriteCount };
  };

  const toggleFavorite = async (car) => {
    if (!car?.id) return null;
    const wasFav = items.some((i) => i.id === car.id);
    const token = getAuthToken();

    if (token) {
      try {
        const r = await api.post(`/cars/${car.id}/favorite`);
        const { favorited, favorite_count: favoriteCount } = r.data;
        clearDelta(car.id);
        setCountOverrides((prev) => ({ ...prev, [car.id]: favoriteCount }));
        setItems((prev) =>
          favorited
            ? [...prev.filter((i) => i.id !== car.id), { id: car.id, price: car.price ?? null }]
            : prev.filter((i) => i.id !== car.id),
        );
        return { favorited, favorite_count: favoriteCount };
      } catch (e) {
        const status = e.response?.status;
        // Régi / távoli backend — nincs még favorite endpoint
        if (status === 404 || status === 405 || status === 501) {
          return toggleFavoriteLocal(car, wasFav);
        }
        if (status === 401) {
          toast.error("Jelentkezz be a kedvencek mentéséhez");
          return null;
        }
        toast.error(e.response?.data?.detail || "Kedvenc mentése sikertelen");
        return null;
      }
    }

    return toggleFavoriteLocal(car, wasFav);
  };

  const isFavorite = (carId) => items.some((i) => i.id === carId);

  const ackPrices = (cars) =>
    setItems((prev) =>
      prev.map((it) => {
        const c = cars.find((x) => x.id === it.id);
        return c ? { ...it, price: c.price } : it;
      }),
    );

  const favorites = items.map((i) => i.id);

  return (
    <FavoritesContext.Provider
      value={{
        favorites,
        priceDrops,
        toggleFavorite,
        isFavorite,
        ackPrices,
        getFavoriteCount,
        count: items.length,
      }}
    >
      {children}
    </FavoritesContext.Provider>
  );
}

export const useFavorites = () => useContext(FavoritesContext);
